/* eslint-disable no-eval */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useContext } from "react";
import { Auth } from "aws-amplify";

import InputField from "components/fields/InputField";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import Logo from "components/Logo";
import Card from "components/card";
import { useAuth } from "./../../routes/hooks/useAuth";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { ProfileContext } from "../../store/ProfileProvider";
import { TokenManager } from "../../utils/tokenManager";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const appName = process.env.REACT_APP_NAME!;
const activityTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const AUTH_VERSION = "2.0.0";

export default function SignIn() {
  const { setProfileTimeZone } = useContext(ProfileContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checkAuthStatus } = useAuth();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [showAutoSignedOutMessage, setShowAutoSignedOutMessage] =
    useState<boolean>(queryParams.get("idle") === "true" ? true : false);

  const [isTwoFactorStep, setIsTwoFactorStep] = useState<boolean>(false);
  const [showVerifyOTPError, setShowVerifyOTPError] = useState<boolean>(false);
  const [showVerifyOTPErrorByUseOnce, setShowVerifyOTPErrorByUseOnce] =
    useState<boolean>(false);
  const [showPrepareError, setShowPrepareError] = useState<boolean>(false);
  const [showExpiredMessage, setShowExpiredMessage] = useState<boolean>(false);

  const [signedInUser, setSignedInUser] = useState<any>(null);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const otpInputRefs = useRef<HTMLInputElement[]>([]);
  const isOTPFormValid = otp.length === 6 ? true : false;

  useEffect(() => {
    document.title = appName + " | Settlement System";

    const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appleTitle) appleTitle.setAttribute("content", appName);

    localStorage.removeItem("subaccountsCount");

    const checkUserState = async () => {
      const authStatus = await checkAuthStatus();
      if (authStatus) {
        localStorage.clear();
        sessionStorage.removeItem("attemptedRefresh");
        Auth.signOut();
      }
    };

    localStorage.removeItem("startIdleCheck");

    const startIdleCheckId = localStorage.getItem("startIdleCheckId");
    clearInterval(startIdleCheckId);
    localStorage.removeItem("startIdleCheckId");

    const startCountdownId = localStorage.getItem("startIdleCheckId");
    clearInterval(startCountdownId);
    localStorage.removeItem("startCountdownId");

    checkUserState();
  }, []);

  useEffect(() => {
    if (queryParams.has("idle") && queryParams.get("idle") === "true") {
      queryParams.delete("idle");
      const updatedUrl = `${location.pathname}${queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      window.history.replaceState({}, "", updatedUrl);
    }
  }, [location.search]);

  useEffect(() => {
    const otpToVerify = otp.join("");
    if (String(otpToVerify).length === 6) {
      handleOTPSubmit();
    }
  }, [otp]);

  useEffect(() => {
    localStorage.setItem("authVersion", AUTH_VERSION);
  }, []);

  const navigate = useNavigate();
  const newPasswordError = !newPassword ? "" : "";
  const confirmPasswordError = newPassword !== confirmPassword ? "Passwords do not match" : "";

  const isFormValid = email !== "" && password !== "";
  const isNewPasswordValid = newPassword && confirmPassword && !newPasswordError && !confirmPasswordError;

  const signInAndUpdate = async (
    isFirstSignin: boolean = false,
    isFirstMFASetup: boolean = false
  ) => {
    try {
      // 1. Get Cognito session
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const cognitoId = idToken.payload.sub;
      const username = idToken.payload["cognito:username"];
      const userGroups = idToken.payload["cognito:groups"];

      // 2. Call auth API
      const responseAuth = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/request`,
        {
          fn: "auth",
          email: email,
          cognitoId: cognitoId,
          isProcessFirstSigninCompleted: isFirstSignin,
          isProcessTwoFactorFirstSetupCompleted: isFirstMFASetup,
        }
      );

      const { token, expires, status, userid, time_zone, userRoles, requiresSelection, isMerchantMultiple } = responseAuth.data;

      // 3. Validate response
      if (status === "Inactive") {
        setError("Your account is inactive, please contact administrator");
        setIsLoading(false);
        return;
      }

      if (!userGroups) {
        setError("User group does not exist. Please contact Admin");
        setIsLoading(false);
        return;
      }

      // 4. Store basic user info (always)
      localStorage.setItem("username", username);
      localStorage.setItem("userid", userid);
      localStorage.setItem("userEmail", email);
      localStorage.setItem("cognitoId", cognitoId);
      localStorage.setItem("time_zone", time_zone || activityTimeZone);
      setProfileTimeZone(time_zone || activityTimeZone);
      localStorage.setItem("userRoles", JSON.stringify(userRoles));

      // 5. Handle routing — Merchant role takes priority
      const merchantRoles = userRoles.filter((r: any) => r.role === "Merchant");
      const hasMerchant = merchantRoles.length > 0;

      if (hasMerchant) {
        const uniqueMAMerchantIds = [...new Set(merchantRoles.map((r: any) => r.merchantId))] as string[];

        if (uniqueMAMerchantIds.length === 1) {
          // Single Merchant merchant — issue Merchant token and go to dashboard
          await navigateAsMerchant(uniqueMAMerchantIds[0], userRoles, username, time_zone);
        } else {
          // Multiple Merchant merchants — need merchant selection
          navigateToMerchantSelection(userRoles, email);
        }
      } else if (!requiresSelection && token) {
        // No Merchant — token already issued
        TokenManager.storeTokens(token, expires, time_zone);
        await navigateToDashboard(userRoles[0], username, time_zone, token);
      } else {
        // Multiple merchants/subaccounts — need selection first
        navigateToSelection(userRoles, email);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error in signInAndUpdate:", error);
      setError("Something went wrong, please try again");
      setIsLoading(false);
    }
  };

  const navigateToDashboard = async (
    userRole: any,
    username: string,
    time_zone: string,
    token: string
  ) => {
    const { role, merchantId, subaccountId, agentId } = userRole;

    localStorage.setItem("userRole", role);
    localStorage.setItem("merchantId", merchantId);
    localStorage.setItem("subaccountId", subaccountId);
    localStorage.setItem("merchantsCount", "1");
    localStorage.setItem("subaccountsCount", "1");

    if (agentId) {
      localStorage.setItem("agentId", agentId);
    }

    let systemLogDetail = "";

    if (role.toLowerCase() === "merchant") {
      localStorage.setItem("subaccountName", "");
      const merchantInfo = await fetchMerchantById(merchantId, token);
      if (merchantInfo) {
        localStorage.setItem("merchantName", merchantInfo.name);
      }
    } else if (role.toLowerCase() !== "superadmin") {
      const pointingId = role.toLowerCase() === "agent" ? agentId : subaccountId;
      const subaccountInfo = await fetchSubaccountById(pointingId, token);
      localStorage.setItem("subaccountName", subaccountInfo.suba_name);
      localStorage.setItem("subaccountType", subaccountInfo.type);
      systemLogDetail = subaccountInfo.suba_name;
      const merchantInfo = await fetchMerchantById(merchantId, token);
      if (merchantInfo) {
        localStorage.setItem("merchantName", merchantInfo.name);
      }
    } else {
      localStorage.setItem("subaccountName", "");
    }

    await createLoginLog(subaccountId, role, username, systemLogDetail, time_zone, token);
    navigate(`/${role.toLowerCase()}/dashboard`);
  };

  const navigateToSelection = (userRoles: any[], email: string) => {
    const merchantIds = Array.from(new Set(userRoles.map(r => r.merchantId)));

    if (merchantIds.length === 1) {
      const merchantId = merchantIds[0];
      const subaccountIds = Array.from(
        new Set(
          userRoles
            .filter(r => r.merchantId === merchantId)
            .map(r => r.subaccountId)
        )
      );

      localStorage.setItem("merchantId", merchantId);
      localStorage.setItem("merchantsCount", "1");
      localStorage.setItem("subaccountsCount", subaccountIds.length.toString());

      navigate("/auth/subaccount-selection", {
        state: { merchantId, cognitoId: "", userRole: userRoles },
      });
    } else {
      localStorage.setItem("merchantsCount", merchantIds.length.toString());
      navigate("/auth/merchant-selection", {
        state: { cognitoId: "", userRole: userRoles },
      });
    }
  };

  const navigateToMerchantSelection = (userRoles: any[], email: string) => {
    const merchantIds = Array.from(new Set(userRoles.map(r => r.merchantId)));
    localStorage.setItem("merchantsCount", merchantIds.length.toString());
    navigate("/auth/merchant-selection", {
      state: { cognitoId: "", userRole: userRoles, isMerchant: true },
    });
  };

  const navigateAsMerchant = async (
    merchantId: string,
    userRoles: any[],
    username: string,
    time_zone: string
  ) => {
    const tokenResponse = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/user/request`,
      {
        fn: "issue-token-after-selection",
        email: email,
        merchantId: merchantId,
        isMerchant: true,
      }
    );

    if (tokenResponse.data.status !== "ok") {
      setError("Failed to authenticate. Please try again.");
      setIsLoading(false);
      return;
    }

    const { token, expires } = tokenResponse.data;
    TokenManager.storeTokens(token, expires, time_zone);

    const allMerchantIds = [...new Set(userRoles.map((r: any) => r.merchantId))];
    localStorage.setItem("userRole", "Merchant");
    localStorage.setItem("merchantId", merchantId);
    localStorage.removeItem("subaccountId");
    localStorage.setItem("subaccountName", "");
    localStorage.setItem("merchantsCount", allMerchantIds.length.toString());
    localStorage.setItem("subaccountsCount", "0");

    const merchantInfo = await fetchMerchantById(merchantId, token);
    if (merchantInfo) {
      localStorage.setItem("merchantName", merchantInfo.name);
    }

    await createLoginLog(null, "Merchant", username, "", time_zone, token);
    navigate("/merchant/dashboard");
  };

  const createLoginLog = async (
    subaccountId: string | null,
    role: string,
    username: string,
    details: string,
    time_zone: string,
    token: string
  ) => {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/${role.toLowerCase()}/systemlog/action/create-log`,
      {
        suba_id: subaccountId,
        action: "login",
        details: details,
        logged_role: role.toLowerCase(),
        logged_username: username,
        current_time_zone: time_zone,
        activity_time_zone: activityTimeZone,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  };

  const handleSubmit = async () => {
    setError(null);
    setShowAutoSignedOutMessage(false);
    if (isFormValid) {
      setIsLoading(true);
      try {
        const user = await Auth.signIn(email, password);
        setSignedInUser(user);

        const getUserTwoFactorInfo = await axios.post(
          process.env.REACT_APP_API_URL + `/api/user/request`,
          {
            fn: "retrieve-user-two-factor-info",
            email: email,
            username: user.username,
          }
        );

        if (getUserTwoFactorInfo.data.status !== "ok") {
          setError("Something went wrong, please try again");
          setIsLoading(false);
          return;
        }

        const isMFAEnabledInPool =
          getUserTwoFactorInfo.data.user.isMFAEnabledInPool;
        const isUserMFAEnabled =
          getUserTwoFactorInfo.data.user.isUserMFAEnabled;

        // User has 2FA enabled - show OTP form
        if (
          isMFAEnabledInPool &&
          isUserMFAEnabled &&
          user.challengeName !== "NEW_PASSWORD_REQUIRED" &&
          (user.preferredMFA === "SOFTWARE_TOKEN_MFA" ||
            user.challengeName === "SOFTWARE_TOKEN_MFA")
        ) {
          setIsTwoFactorStep(true);
          setIsLoading(false);
        } else if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          setIsLoading(false);
          setIsFirstLogin(true);
        } else {
          await signInAndUpdate();
        }
      } catch (error: any) {
        console.error("Error signing in", error);
        if (String(error).includes("disabled")) {
          setError(
            "Your account has been disabled, please contact the administrator for assistance"
          );
        } else if (String(error).includes("NotAuthorizedException") || String(error).includes("UserNotFoundException")) {
          setError("Incorrect username or password, please try again");
        } else {
          setError("We had trouble processing your request, please try again");
        }
        setIsLoading(false);
      }
    }
  };

  const handleNewPasswordSubmit = async () => {
    setError(null);
    setShowPrepareError(false);
    setShowAutoSignedOutMessage(false);
    if (isNewPasswordValid) {
      setIsLoading(true);
      try {
        const user = await Auth.signIn(email, password);
        await Auth.completeNewPassword(user, newPassword);
        await setupTOTP(user);
        await signInAndUpdate(true);
      } catch (error: any) {
        console.error("Error setting new password", error);
        if (String(error).includes("NotAuthorizedException") || String(error).includes("InvalidPasswordException")) {
          setError("Password does not meet requirements. Please ensure it contains uppercase, lowercase, numbers, and special characters.");
        } else {
          setError("We had trouble processing your request, please try again");
          setShowPrepareError(true);
        }
        setIsLoading(false);
      }
    }
  };

  const handleOTPSubmit = async () => {
    setIsLoading(true);
    setShowVerifyOTPError(false);
    setShowVerifyOTPErrorByUseOnce(false);
    const otpToVerify = otp.join("");
    verifyMFA(otpToVerify);
  };

  const verifyMFA = async (otp: string) => {
    try {
      await Auth.confirmSignIn(signedInUser, otp, "SOFTWARE_TOKEN_MFA");
      await signInAndUpdate();
    } catch (error: any) {
      setIsLoading(false);
      if (String(error.message).includes("once")) {
        setShowVerifyOTPErrorByUseOnce(true);
      } else if (String(error.message).includes("expired")) {
        setShowExpiredMessage(true);
      } else {
        setShowVerifyOTPError(true);
      }
      console.error("Error during OTP verification:", error);
    }
  };

  const fetchSubaccountById = async (id: string, token: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.subaccount.payload;
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data", error);
    }
  };

  const fetchMerchantById = async (id: string, token: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/merchant/get-merchant-by-id?merchantid=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.merchant?.payload;
    } catch (error) {
      console.error("Error fetching merchant data", error);
    }
  };

  const navigateToForgotPassword = async () => {
    navigate(`/auth/forgot-password`);
  };

  const setupTOTP = async (user: any) => {
    try {
      const totpSecret = await Auth.setupTOTP(user);
      return totpSecret;
    } catch (error) {
      console.error("Error setting up TOTP:", error);
    }
  };

  const handleOTPInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setShowVerifyOTPError(false);
    setShowVerifyOTPErrorByUseOnce(false);
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        otpInputRefs.current[index + 1].focus();
      } else if (index === 5) {
        otpInputRefs.current[index].blur();
      }
    }
  };

  const handleOTPInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    setShowVerifyOTPError(false);
    setShowVerifyOTPErrorByUseOnce(false);
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        otpInputRefs.current[index - 1].focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handleOTPInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    setShowVerifyOTPError(false);
    setShowVerifyOTPErrorByUseOnce(false);
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").slice(0, 6);
    const newOtp = pasteData
      .split("")
      .map((char, i) => (i < 6 ? char : otp[i]));
    setOtp(newOtp);

    pasteData.split("").forEach((_, i) => {
      if (i < 5 && pasteData[i]) {
        otpInputRefs.current[i + 1].focus();
      } else if (i === 5) {
        otpInputRefs.current[i].blur();
      }
    });
  };

  const onInputChange = (input: string, value: any) => {
    setError("");
    setShowAutoSignedOutMessage(false);
    if (input === "email") {
      setEmail(value);
    } else if (input === "password") {
      setPassword(value);
    }
  };

  const onNewPasswordChange = (value: string) => {
    setError("");
    setShowAutoSignedOutMessage(false);
    setNewPassword(value);
  };

  const onConfirmPasswordChange = (value: string) => {
    setError("");
    setShowAutoSignedOutMessage(false);
    setConfirmPassword(value);
  };

  const resetToSignInAgain = async () => {
    localStorage.clear();

    const authStatus = await checkAuthStatus();
    if (authStatus) {
      localStorage.clear();
      sessionStorage.removeItem("attemptedRefresh");
      Auth.signOut();
    }

    setOtp(Array(6).fill(""));
    setShowVerifyOTPErrorByUseOnce(false);
    setShowVerifyOTPError(false);
    setShowPrepareError(false);
    setShowExpiredMessage(false);
    setIsTwoFactorStep(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      if (isFirstLogin && isNewPasswordValid) {
        handleNewPasswordSubmit();
      } else if (!isFirstLogin && isFormValid) {
        handleSubmit();
      }
    }
  };

  return (
    <div className="mb-16 mt-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center">
      <div className="mt-[10vh] w-full max-w-[420px] flex-col items-center md:pl-4 lg:pl-0">
        <>
          <div className="mb-12 flex items-center justify-center">
            <Logo light={true} width="229px" />
          </div>
          {!isTwoFactorStep && (
            <Card className="shadow-cs bg-white p-8 rounded-lg">
              <h4 className="mb-2 text-2xl font-bold text-navy-700">Welcome</h4>
              <p className="mb-8 text-base text-gray-600">
                {isFirstLogin ? "Please set your new password" : "Enter your login credentials to continue"}
              </p>

              {!isFirstLogin && (
                <>
                  <InputField
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    variant="auth"
                    extra="mb-6"
                    label="Email"
                    placeholder=""
                    id="email"
                    type="text"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => onInputChange("email", e.target.value)}
                  />

                  <InputField
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    variant="auth"
                    extra="mb-3"
                    label="Password"
                    placeholder=""
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => onInputChange("password", e.target.value)}
                  />
                </>
              )}

              {isFirstLogin && (
                <>
                  <InputField
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    variant="auth"
                    extra="mb-6"
                    label="New Password"
                    placeholder="Enter your new password"
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => onNewPasswordChange(e.target.value)}
                    error={newPasswordError}
                  />

                  <InputField
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    variant="auth"
                    extra="mb-3"
                    label="Confirm Password"
                    placeholder="Confirm your new password"
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => onConfirmPasswordChange(e.target.value)}
                    error={confirmPasswordError}
                  />
                </>
              )}

              {error && (
                <p className="flex justify-center text-sm text-red-500">
                  {error}
                </p>
              )}

              {showAutoSignedOutMessage && (
                <p className="my-2 flex justify-center text-sm text-red-500">
                  You have been automatically signed out due to inactivity
                </p>
              )}

              {isFirstLogin ? (
                <Button
                  onClick={handleNewPasswordSubmit}
                  isDisabled={!isNewPasswordValid || isLoading}
                  isLoading={isLoading}
                  loadingText="Please wait..."
                  className="linear mt-2 w-full rounded-xl bg-primary-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:hover:bg-primary-300 dark:active:bg-primary-200"
                >
                  Set your new password
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleSubmit}
                    isDisabled={!isFormValid || isLoading}
                    isLoading={isLoading}
                    loadingText="Please wait..."
                    className="linear mt-2 w-full rounded-lg  bg-primary-500 !py-3 text-base font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:hover:bg-primary-300 dark:active:bg-primary-200"
                  >
                    Sign In
                  </Button>
                  <div className="mt-3 flex items-center justify-center">
                    <a
                      onClick={
                        isLoading ? undefined : () => navigateToForgotPassword()
                      }
                      className={`text-center text-sm text-navy-700 ${isLoading
                          ? "cursor-not-allowed opacity-50"
                          : "hover:cursor-pointer hover:underline"
                        }`}
                    >
                      Forgot your password?
                    </a>
                  </div>
                </>
              )}
            </Card>
          )}
          {isTwoFactorStep && (
            <Card className="shadow-cs bg-white p-8 text-center sm:rounded-md">
              <h4 className="mb-4 text-2xl font-bold text-navy-700">
                Two-Factor Authentication
              </h4>

              {!showExpiredMessage && (
                <>
                  <p className="my-3 text-sm">
                    Enter the code from your authentication app
                  </p>
                  <div className="my-4 flex w-full items-center justify-center space-x-2 text-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          if (el) otpInputRefs.current[index] = el;
                        }}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOTPInputChange(e, index)}
                        onKeyDown={(e) => handleOTPInputKeyDown(e, index)}
                        onPaste={handleOTPInputPaste}
                        className="h-12 w-10 rounded-md border border-gray-300 text-center shadow-sm outline-none"
                      />
                    ))}
                  </div>
                </>
              )}

              {showVerifyOTPError && (
                <p className="my-2 flex justify-center text-sm text-red-500">
                  Invalid Code, please try again
                </p>
              )}

              {showVerifyOTPErrorByUseOnce && (
                <p className="my-2 flex justify-center text-sm text-red-500">
                  Invalid Code, it has already been used
                </p>
              )}

              {showPrepareError && (
                <p className="my-2 flex justify-center text-sm text-red-500">
                  Something went wrong, please try again later
                </p>
              )}

              {showExpiredMessage && (
                <p className="my-2 flex justify-center text-sm text-red-500">
                  Your session has expired due to inactivity
                  <br />
                  Please sign in again
                </p>
              )}

              {!showExpiredMessage && (
                <Button
                  onClick={handleOTPSubmit}
                  isDisabled={!isOTPFormValid || isLoading}
                  isLoading={isLoading}
                  loadingText="Please wait..."
                  className="linear mt-2 w-full rounded-lg bg-primary-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:hover:bg-primary-300 dark:active:bg-primary-200"
                >
                  Verify
                </Button>
              )}

              {showExpiredMessage && (
                <div className="mt-3 flex items-center justify-center">
                  <a
                    onClick={() => resetToSignInAgain()}
                    className="text-center text-sm text-navy-700 hover:cursor-pointer hover:underline"
                  >
                    Sign in again
                  </a>
                </div>
              )}
            </Card>
          )}
        </>
      </div>
    </div>
  );
}